import { useStyles } from '../../../styles/NewOrUpdateForm.styles';
import { Grid, FormControl, Typography, Autocomplete, TextField, Chip } from '@mui/material';
import { isEqual } from 'lodash';
import { type SyntheticEvent, useState } from 'react';
import type { FormComponentProps } from '@components/forms/types';

type SingleSelectProps<State extends Record<string, any>> = FormComponentProps<State> & {
    options: { name: string; id: string }[];
    label: string;
    nameKey: keyof State;
    idKey: keyof State;
    error?: boolean;
    onChange?: (_: SyntheticEvent<Element, Event>, value: any) => void;
    disable?: boolean;
    size?: number;
    includeAll?: boolean;
};

export const FormSingleSelect = <State extends Record<string, any>>({
    options,
    label,
    onChange,
    error,
    idKey,
    nameKey,
    draft,
    disable,
    size,
    includeAll = true,
}: SingleSelectProps<State>) => {
    const classes = useStyles();
    const [draftState, setDraftState] = draft;
    const [open, setOpen] = useState<boolean>(false);

    const allOption = includeAll ? [{ id: '*', name: 'All ' }, ...options] : options;

    const handleOpen = () => {
        if (!disable) setOpen(true);
    };

    return (
        <Grid item md={size ?? 6} xs={12} className={classes.textInputWrapper}>
            <Typography variant='body2' color={error ? 'error' : 'text.primary'} fontWeight={1000} sx={{ pl: 0.5 }}>
                {label}
            </Typography>

            <FormControl fullWidth disabled={disable} error={error}>
                <Autocomplete
                    open={open}
                    multiple
                    size={'small'}
                    openOnFocus
                    disabled={disable}
                    value={
                        allOption?.filter(({ id }) => {
                            const match =
                                typeof draftState?.[idKey] === 'number'
                                    ? parseInt(id) === draftState?.[idKey]
                                    : id === draftState?.[idKey];
                            return match;
                        }) || []
                    }
                    options={allOption || []}
                    getOptionLabel={({ name }) => name}
                    filterSelectedOptions
                    onChange={
                        onChange ??
                        ((_: SyntheticEvent<Element, Event>, value) => {
                            const { id, name } = value.pop() ?? {};

                            const updated =
                                idKey === nameKey
                                    ? { ...draftState, [nameKey]: name }
                                    : {
                                          ...draftState,
                                          [idKey]: id && parseInt(id) ? parseInt(id) : id,
                                          [nameKey]: name,
                                      };

                            setDraftState(updated);
                        })
                    }
                    onOpen={handleOpen}
                    onClose={() => setOpen(false)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant='outlined'
                            placeholder={`Select ${label}`}
                            InputLabelProps={{ shrink: false }}
                        />
                    )}
                    ChipProps={{
                        sx: { pointerEvents: disable ? 'none' : 'auto' },
                    }}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.name}
                        </li>
                    )}
                    renderTags={(selected, getTagProps) =>
                        selected.map((value, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={index}
                                label={value.name}
                                size='small'
                                color='primary'
                                disabled={disable}
                                onClick={(e) => disable && e.stopPropagation()}
                            />
                        ))
                    }
                    isOptionEqualToValue={(option, value) => isEqual(option, value)}
                />
            </FormControl>
        </Grid>
    );
};
